/**
 * Mobile Wallet Detection Utilities
 * ===================================
 * Detects in-app wallet browsers (like Xova, MetaMask Mobile, Trust Wallet, etc.)
 * and provides polling for slow `window.ethereum` injection on mobile WebViews.
 *
 * WHY THIS EXISTS:
 * Mobile wallet apps embed a browser (WebView) that injects `window.ethereum`.
 * Unlike desktop extensions, these in-app browsers:
 *   1. Often don't support EIP-6963 announcements
 *   2. Can be slower to inject the provider (WebView init lag)
 *   3. May not set the same branding flags as the desktop extension
 *
 * This utility solves all three by detecting the environment early,
 * polling for the provider with retries, and identifying the wallet
 * from whatever signals are available.
 *
 * @version 1.0.0 - Ported for SportsFuture
 */

// =============================================================================
// TYPES
// =============================================================================

export interface MobileWalletInfo {
  /** Whether we're in any mobile wallet's in-app browser */
  isMobileWalletBrowser: boolean;
  /** Whether we're on a mobile device at all */
  isMobile: boolean;
  /** Detected wallet name from UA or provider flags (null if unknown) */
  detectedWalletName: string | null;
  /** Matched RDNS for deduplication against EIP-6963 wallets */
  detectedRdns: string | null;
}

/** Known mobile wallet user-agent signatures → [displayName, rdns] */
const MOBILE_WALLET_UA_SIGNATURES: Array<[RegExp, string, string]> = [
  // [regex to match in UA, display name, rdns for dedup]
  [/XovaWallet|Xova/i,             'Xova Wallet',     'com.xovawallet'],
  [/MetaMaskMobile|MetaMask\/\d/i,  'MetaMask',        'io.metamask'],
  [/CoinbaseWallet/i,               'Coinbase Wallet', 'com.coinbase.wallet'],
  [/TrustWallet|Trust\/\d/i,        'Trust Wallet',    'com.trustwallet.app'],
  [/RainbowApp|Rainbow/i,           'Rainbow',         'me.rainbow'],
  [/OKApp|OKX/i,                    'OKX Wallet',      'com.okex.wallet'],
  [/PhantomMobile|Phantom/i,        'Phantom',         'app.phantom'],
  [/CoreWallet|AvalancheCore/i,     'Core Wallet',     'app.core'],
  [/imToken/i,                      'imToken',         'im.token.app'],
  [/TokenPocket/i,                  'TokenPocket',     'pro.tokenpocket'],
];

/** Provider-level flags to check on `window.ethereum` (fallback if UA doesn't match) */
const PROVIDER_FLAG_MAP: Array<[string, string, string]> = [
  // [flag on window.ethereum, display name, rdns]
  ['isXova',           'Xova Wallet',     'com.xovawallet'],
  ['isXovaWallet',     'Xova Wallet',     'com.xovawallet'],
  ['isMetaMask',       'MetaMask',        'io.metamask'],
  ['isCoinbaseWallet', 'Coinbase Wallet', 'com.coinbase.wallet'],
  ['isTrust',          'Trust Wallet',    'com.trustwallet.app'],
  ['isTrustWallet',    'Trust Wallet',    'com.trustwallet.app'],
  ['isBraveWallet',    'Brave Wallet',    'com.brave.wallet'],
  ['isRabby',          'Rabby Wallet',    'io.rabby'],
  ['isCore',           'Core Wallet',     'app.core'],
  ['isTokenPocket',    'TokenPocket',     'pro.tokenpocket'],
  ['isImToken',        'imToken',         'im.token.app'],
  ['isPhantom',        'Phantom',         'app.phantom'],
  ['isOKExWallet',     'OKX Wallet',      'com.okex.wallet'],
  ['isOkxWallet',      'OKX Wallet',      'com.okex.wallet'],
];

// =============================================================================
// CORE DETECTION
// =============================================================================

/**
 * Detect whether we're on a mobile device.
 * Uses UA + touch/screen heuristics for reliability.
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';

  // UA-based check
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(ua);

  // Heuristic: touch support + small screen (catches tablets too)
  const touchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const smallScreen = window.innerWidth <= 1024;

  return mobileUA || (touchScreen && smallScreen);
}

/**
 * Try to identify a wallet from the User-Agent string.
 * Returns [name, rdns] or null if no match.
 */
function detectWalletFromUA(): [string, string] | null {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent || '';

  for (const [regex, name, rdns] of MOBILE_WALLET_UA_SIGNATURES) {
    if (regex.test(ua)) {
      return [name, rdns];
    }
  }

  return null;
}

/**
 * Try to identify a wallet from flags on the provider object.
 * Returns [name, rdns] or null if no match.
 */
function detectWalletFromProvider(provider: any): [string, string] | null {
  if (!provider) return null;

  for (const [flag, name, rdns] of PROVIDER_FLAG_MAP) {
    if (provider[flag]) {
      return [name, rdns];
    }
  }

  return null;
}

/**
 * Full mobile wallet environment detection.
 *
 * Checks UA first (fast, available before `window.ethereum` injects),
 * then falls back to provider flags if available.
 */
export function detectMobileWallet(): MobileWalletInfo {
  const mobile = isMobileDevice();

  // Try UA-based detection first (available immediately, no provider needed)
  const uaMatch = detectWalletFromUA();
  if (uaMatch) {
    return {
      isMobileWalletBrowser: true,
      isMobile: mobile,
      detectedWalletName: uaMatch[0],
      detectedRdns: uaMatch[1],
    };
  }

  // Try provider flags (window.ethereum might already be available)
  if (typeof window !== 'undefined' && window.ethereum) {
    const providerMatch = detectWalletFromProvider(window.ethereum);
    if (providerMatch && mobile) {
      return {
        isMobileWalletBrowser: true,
        isMobile: mobile,
        detectedWalletName: providerMatch[0],
        detectedRdns: providerMatch[1],
      };
    }
  }

  // No specific wallet detected, but if mobile + ethereum exists → likely in-app browser
  const hasEthereum = typeof window !== 'undefined' && !!window.ethereum;

  return {
    isMobileWalletBrowser: mobile && hasEthereum,
    isMobile: mobile,
    detectedWalletName: mobile && hasEthereum ? 'Browser Wallet' : null,
    detectedRdns: null,
  };
}

// =============================================================================
// PROVIDER POLLING (for slow mobile WebView injection)
// =============================================================================

export interface PollOptions {
  /** Interval between checks in ms (default: 200) */
  intervalMs?: number;
  /** Maximum time to wait in ms (default: 3000 on mobile, 1000 on desktop) */
  maxWaitMs?: number;
}

/**
 * Poll for `window.ethereum` to appear.
 *
 * Returns the provider as soon as it's found, or null if the timeout expires.
 * On mobile, waits longer by default since WebView injection is slower.
 *
 * Usage:
 *   const provider = await pollForProvider();
 *   if (provider) { // ready to use }
 */
export function pollForProvider(options: PollOptions = {}): Promise<any | null> {
  const mobile = isMobileDevice();
  const intervalMs = options.intervalMs ?? 200;
  const maxWaitMs = options.maxWaitMs ?? (mobile ? 3000 : 1000);

  return new Promise((resolve) => {
    // Check immediately
    if (typeof window !== 'undefined' && window.ethereum) {
      resolve(window.ethereum);
      return;
    }

    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += intervalMs;

      if (typeof window !== 'undefined' && window.ethereum) {
        clearInterval(timer);
        resolve(window.ethereum);
        return;
      }

      if (elapsed >= maxWaitMs) {
        clearInterval(timer);
        resolve(null);
      }
    }, intervalMs);
  });
}

// =============================================================================
// DEDUPLICATION HELPER
// =============================================================================

/**
 * Check whether a legacy `window.ethereum` wallet is already represented
 * in the EIP-6963 discovered wallet list.
 *
 * Compares by:
 *   1. Provider object identity (===)
 *   2. RDNS match
 *
 * This prevents showing "MetaMask" twice — once from EIP-6963 and once
 * from the legacy fallback.
 */
export function isProviderAlreadyDiscovered(
  eip6963Wallets: Array<{ rdns: string | null; provider: any }>,
  legacyProvider: any,
  legacyRdns: string | null
): boolean {
  for (const w of eip6963Wallets) {
    // Same provider reference — definitely a duplicate
    if (w.provider && w.provider === legacyProvider) return true;

    // Same RDNS (and not the generic fallback) — very likely the same wallet
    if (legacyRdns && legacyRdns !== 'unknown.injected' && w.rdns === legacyRdns) return true;
  }

  return false;
}
